import { inject, Injectable } from '@angular/core';
import { deleteField } from '@angular/fire/firestore';
import { FirestoreAdapter } from '@shared/firestore/firestore-adapter';
import { firstValueFrom, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  EngagementTriggerType,
  IEngagementRule,
  RuleSentiment,
} from '../models/IEngagementRule';

/** Trigger types limited to one rule per location (see assertExclusiveTriggerAllowed). */
export const EXCLUSIVE_SINGLE_TRIGGER_TYPES: readonly EngagementTriggerType[] =
  [
    'REFERRED_FRIEND_WELCOME',
    'ENROLLMENT_REWARD',
    'OFFER_EXPIRING',
    'GOOGLE_REVIEW_DEFAULT',
    'BIRTHDAY',
  ] as const;
import { SharedService } from '../../services/shared.service';

export interface RuleTemplate {
  triggerType: EngagementTriggerType;
  label: string;
  defaultGoal: string;
  defaultDelayHours: number;
  /** Suggested sentiment for this template (conversation-driven matching). */
  defaultSentiment: RuleSentiment;
  /** Suggested priority (higher wins when multiple rules match). */
  defaultPriority: number;
  /** Optional suggested keywords for matching. */
  defaultKeywords?: string[];
  /** Optional default visit number (e.g. 1 for first-visit rules). */
  defaultVisitNumber?: number | null;
}

export type DefaultRuleSet = 'review_chase' | 'full_trigger_map';

/**
 * Goal copy for the merged Enrollment Reward rule. Grants its offer
 * immediately on sign-up (both the enrollment link and the in-store QR
 * sign-up), regardless of sentiment.
 */
export const ENROLLMENT_REWARD_GOAL =
  'Welcome the new customer on sign-up and grant the offer immediately as a ' +
  'first-visit thank-you, regardless of sentiment. Applies to both the ' +
  'enrollment link and the in-store QR sign-up.';

/** Display label for the merged enrollment/first-visit reward rule. */
export const ENROLLMENT_REWARD_LABEL = 'Enrollment Reward';

/**
 * Default precoded BIRTHDAY message per language. BIRTHDAY copy is
 * deterministic (not AI generated); managers may edit it under the rule.
 * Supported tokens: {firstName}, {offer}, {portalLink}, {phone}. A line that
 * contains {offer}, {portalLink} or {phone} is dropped entirely when that
 * value is empty, so those segments only appear when relevant.
 */
export const DEFAULT_BIRTHDAY_MESSAGE: { en: string; es: string } = {
  en:
    'Happy birthday, {firstName}! We wanted to celebrate you on your special day.\n' +
    'Your birthday treat: {offer}.\n' +
    'We hope you have a wonderful day!\n' +
    '\n' +
    "Give us a call to set your next appointment — we can't wait to see you again!\n" +
    'Call us at {phone}.\n' +
    '\n' +
    'You can view your offer anytime by checking it on your client portal: {portalLink}',
  es:
    '¡Feliz cumpleaños, {firstName}! Queríamos celebrarte en tu día especial.\n' +
    'Tu regalo de cumpleaños: {offer}.\n' +
    '¡Esperamos que tengas un día maravilloso!\n' +
    '\n' +
    'Llámanos para agendar tu próxima cita — ¡no podemos esperar a verte de nuevo!\n' +
    'Llámanos al {phone}.\n' +
    '\n' +
    'Puedes ver tu oferta en cualquier momento revisándola en tu portal de cliente: {portalLink}',
};

export function getEngagementRuleDisplayLabel(
  rule: Pick<IEngagementRule, 'triggerType' | 'visitNumber'>,
  templates: RuleTemplate[] = RULE_TEMPLATES,
): string {
  const triggerType = rule.triggerType;
  if (!triggerType) return '—';
  return (
    templates.find((t) => t.triggerType === triggerType)?.label ?? triggerType
  );
}

export const RULE_TEMPLATES: RuleTemplate[] = [
  {
    triggerType: 'POST_VISIT_HAPPY',
    label: 'Post Visit Happy',
    defaultGoal:
      'Thank the customer for their positive experience and show genuine appreciation',
    defaultDelayHours: 0,
    defaultSentiment: 'positive',
    defaultPriority: 10,
    defaultKeywords: [],
  },
  {
    // Per-location review toggle (not a competitive rule). Only `enabled`
    // and `maxNudges` are read at runtime; the sentiment / priority /
    // keywords defaults below are inert for this template.
    triggerType: 'GOOGLE_REVIEW_DEFAULT',
    label: 'Review Request',
    defaultGoal:
      'When enabled, the AI Concierge will send a Google review request as ' +
      'a separate follow-up message right after the first eligible reply of ' +
      'each visit cycle, in addition to whichever post-visit rule fired. ' +
      'The customer is asked at most once per visit (sign-up and the first ' +
      'recorded visit count as the same cycle). Use Max review nudges to cap ' +
      'how many different visits will produce a request on this conversation.',
    defaultDelayHours: 0,
    defaultSentiment: 'any',
    defaultPriority: 0,
    defaultKeywords: [],
  },
  {
    triggerType: 'POST_VISIT_NEUTRAL',
    label: 'Post Visit Neutral',
    defaultGoal:
      'Ask what could have gone better and show genuine interest in improving',
    defaultDelayHours: 0,
    defaultSentiment: 'neutral',
    defaultPriority: 15,
    defaultKeywords: [
      'okay',
      'fine',
      'alright',
      'average',
      'not bad',
      'decent',
    ],
  },
  {
    triggerType: 'POST_VISIT_UNHAPPY',
    label: 'Post Visit Negative',
    defaultGoal: 'Apologize and offer to make it right',
    defaultDelayHours: 0,
    defaultSentiment: 'negative',
    defaultPriority: 20,
    defaultKeywords: [],
  },
  {
    triggerType: 'VISIT_MILESTONE',
    label: 'Visit Milestone',
    defaultGoal:
      'Celebrate the customer reaching this visit milestone. Acknowledge the visit number, ' +
      'express genuine appreciation for their loyalty, and grant the reward. ' +
      'Regardless of sentiment, always lead with the milestone celebration — ' +
      'if the customer had a bad experience, empathize first then pivot to the milestone.',
    defaultDelayHours: 0,
    defaultSentiment: 'any',
    defaultPriority: 50,
    defaultKeywords: [],
  },
  {
    triggerType: 'AMOUNT_SPENT',
    label: 'Amount Spent Milestone',
    defaultGoal:
      'Thank the customer for their loyalty and reward them for reaching a spending milestone',
    defaultDelayHours: 0,
    defaultSentiment: 'any',
    defaultPriority: 5,
    defaultKeywords: [],
  },
  {
    triggerType: 'REFERRAL_MILESTONE',
    label: 'Referral Milestone',
    defaultGoal: 'Thank customer for referrals and offer a reward',
    defaultDelayHours: 0,
    defaultSentiment: 'any',
    defaultPriority: 5,
    defaultKeywords: [],
  },
  {
    triggerType: 'REFERRED_FRIEND_WELCOME',
    label: 'Referred Friend Welcome',
    defaultGoal: 'Welcome the referred friend and offer a reward for visiting',
    defaultDelayHours: 0,
    defaultSentiment: 'any',
    defaultPriority: 5,
    defaultKeywords: [],
  },
  {
    triggerType: 'ENROLLMENT_REWARD',
    label: ENROLLMENT_REWARD_LABEL,
    defaultGoal: ENROLLMENT_REWARD_GOAL,
    defaultDelayHours: 0,
    defaultSentiment: 'any',
    defaultPriority: 5,
    defaultKeywords: [],
  },
  {
    triggerType: 'INACTIVE_WINBACK',
    label: 'Inactive Customer Win-Back',
    defaultGoal: 'Win back the customer with a personalized message and offer',
    defaultDelayHours: 0,
    defaultSentiment: 'any',
    defaultPriority: 0,
    defaultKeywords: [],
  },
  {
    triggerType: 'OFFER_EXPIRING',
    label: 'Offer Expiring Reminder',
    defaultGoal:
      'Remind the customer their offer is expiring soon and encourage redemption',
    defaultDelayHours: 0,
    defaultSentiment: 'any',
    defaultPriority: 0,
    defaultKeywords: [],
  },
  {
    triggerType: 'BIRTHDAY',
    label: 'Birthday',
    defaultGoal:
      'Wish the customer a happy birthday and make them feel valued with a special birthday offer',
    defaultDelayHours: 0,
    defaultSentiment: 'any',
    defaultPriority: 5,
    defaultKeywords: [],
  },
  {
    triggerType: 'CUSTOM',
    label: 'Custom',
    defaultGoal: '',
    defaultDelayHours: 0,
    defaultSentiment: 'any',
    defaultPriority: 0,
    defaultKeywords: [],
  },
];

@Injectable({
  providedIn: 'root',
})
export class EngagementRulesService {
  private firestore = inject(FirestoreAdapter);
  private sharedService = inject(SharedService);

  private collection(locationId: string) {
    return this.firestore
      .collection('locations')
      .doc(locationId)
      .collection('engagementRules');
  }

  getTemplates(): RuleTemplate[] {
    return RULE_TEMPLATES;
  }

  /**
   * Coerce the legacy `ENROLLMENT_LINK_WELCOME` trigger type to the merged
   * `ENROLLMENT_REWARD` so rule docs not yet touched by the one-off migration
   * still render and save under the canonical type.
   */
  private coerceLegacyTriggerType(rule: IEngagementRule): IEngagementRule {
    if ((rule.triggerType as string) === 'ENROLLMENT_LINK_WELCOME') {
      return { ...rule, triggerType: 'ENROLLMENT_REWARD' };
    }
    return rule;
  }

  list(locationId: string): Observable<IEngagementRule[]> {
    if (!locationId) return of([]);
    return this.collection(locationId)
      .get()
      .pipe(
        map((snap) =>
          snap.docs.map((d) =>
            this.coerceLegacyTriggerType({
              id: d.id,
              ...d.data(),
            } as IEngagementRule),
          ),
        ),
        catchError((err) => {
          this.sharedService.logErrors(
            err,
            'listing engagement rules',
            'EngagementRulesService',
          );
          return of([]);
        }),
      );
  }

  list$(locationId: string): Observable<IEngagementRule[]> {
    if (!locationId) return of([]);
    return this.collection(locationId)
      .snapshotChanges()
      .pipe(
        map((changes) =>
          changes.map((c) => {
            const d = c.payload.doc;
            return this.coerceLegacyTriggerType({
              id: d.id,
              ...d.data(),
            } as IEngagementRule);
          }),
        ),
        catchError((err) => {
          this.sharedService.logErrors(
            err,
            'subscribing to engagement rules',
            'EngagementRulesService',
          );
          return of([]);
        }),
      );
  }

  /** Remove undefined values so Firestore accepts the payload (it rejects undefined). */
  private stripUndefined<T extends Record<string, unknown>>(
    obj: T,
  ): Partial<T> {
    return Object.fromEntries(
      Object.entries(obj).filter(([, v]) => v !== undefined),
    ) as Partial<T>;
  }

  /**
   * Welcome and offer-expiring rules are limited to one per location (product + backend consistency).
   */
  private async assertExclusiveTriggerAllowed(
    locationId: string,
    rule: Pick<IEngagementRule, 'triggerType' | 'visitNumber'>,
    excludeRuleId?: string,
  ): Promise<void> {
    const triggerType = rule.triggerType;
    if (
      triggerType &&
      (EXCLUSIVE_SINGLE_TRIGGER_TYPES as readonly string[]).includes(
        triggerType,
      )
    ) {
      const snap = await firstValueFrom(this.collection(locationId).get());
      const dup = snap.docs.some((d) => {
        if (excludeRuleId && d.id === excludeRuleId) return false;
        const row = d.data() as IEngagementRule;
        return row.triggerType === triggerType;
      });
      if (dup) {
        const messages: Partial<Record<EngagementTriggerType, string>> = {
          OFFER_EXPIRING:
            'Only one Offer Expiring Reminder is allowed per location.',
          GOOGLE_REVIEW_DEFAULT:
            'Only one Review Request rule is allowed per location.',
          BIRTHDAY: 'Only one Birthday rule is allowed per location.',
          ENROLLMENT_REWARD:
            'Only one Enrollment Reward rule is allowed per location.',
        };
        throw new Error(
          messages[triggerType] ??
            'Only one Referred Friend Welcome rule is allowed per location.',
        );
      }
    }
  }

  async add(
    locationId: string,
    rule: Omit<IEngagementRule, 'id'>,
  ): Promise<string> {
    await this.assertExclusiveTriggerAllowed(locationId, rule);
    const col = this.collection(locationId);
    const { id: _id, ...data } = rule as IEngagementRule;
    const docRef = await col.add(
      this.stripUndefined(data as Record<string, unknown>),
    );
    return docRef.id;
  }

  /**
   * Fields that are nullable in the rule model. When the form explicitly sets
   * them to `undefined`, the old Firestore value must be deleted — not silently
   * preserved (which is what stripUndefined + Firestore update() would do).
   */
  private static readonly NULLABLE_FIELDS: readonly string[] = [
    'visitNumber',
    'referralCount',
    'inactiveDays',
    'reminderDaysBefore',
    'amountSpentThreshold',
    'keywords',
    'recurring',
    'maxNudges',
  ];

  /**
   * For GOOGLE_REVIEW_DEFAULT rules the only meaningful runtime fields are
   * `enabled` and `maxNudges` — every other rule-matching field (sentiment,
   * priority, keywords, eligibleOfferIds, visit/referral/inactive thresholds,
   * recurring, etc.) is ignored by the backend because this rule is no longer
   * a competitive rule; it's a per-location toggle for the deterministic
   * review-nudge. Strip those fields actively from Firestore on save so
   * existing rule docs get cleaned up.
   */
  private static readonly REVIEW_TOGGLE_STRIPPED_FIELDS: readonly string[] = [
    'sentiment',
    'priority',
    'keywords',
    'eligibleOfferIds',
    'visitNumber',
    'referralCount',
    'inactiveDays',
    'reminderDaysBefore',
    'amountSpentThreshold',
    'recurring',
  ];

  private defaultRuleMatches(
    existing: IEngagementRule,
    template: Omit<IEngagementRule, 'id'>,
  ): boolean {
    if (existing.triggerType !== template.triggerType) return false;

    switch (template.triggerType) {
      case 'VISIT_MILESTONE':
        return existing.visitNumber === template.visitNumber;
      case 'AMOUNT_SPENT':
        return existing.amountSpentThreshold === template.amountSpentThreshold;
      case 'REFERRAL_MILESTONE':
        return existing.referralCount === template.referralCount;
      case 'INACTIVE_WINBACK':
        return existing.inactiveDays === template.inactiveDays;
      case 'OFFER_EXPIRING':
        return existing.reminderDaysBefore === template.reminderDaysBefore;
      case 'POST_VISIT_HAPPY':
      case 'POST_VISIT_NEUTRAL':
      case 'POST_VISIT_UNHAPPY':
        return existing.sentiment === template.sentiment;
      case 'GOOGLE_REVIEW_DEFAULT':
      case 'BIRTHDAY':
      case 'ENROLLMENT_REWARD':
      case 'REFERRED_FRIEND_WELCOME':
        return true;
      default:
        return false;
    }
  }

  private defaultRuleResetPayload(
    rule: Omit<IEngagementRule, 'id'>,
  ): Partial<IEngagementRule> {
    const payload = { ...rule } as Partial<IEngagementRule> &
      Record<string, unknown>;
    for (const field of EngagementRulesService.NULLABLE_FIELDS) {
      if (!(field in payload)) payload[field] = undefined;
    }
    return payload;
  }

  private defaultRuleUpdatePayload(
    rule: Omit<IEngagementRule, 'id'>,
  ): Record<string, unknown> {
    const raw = this.defaultRuleResetPayload(rule) as Record<string, unknown>;
    const fieldDeletes: Record<string, unknown> = {};

    for (const field of EngagementRulesService.NULLABLE_FIELDS) {
      if (raw[field] === undefined) {
        fieldDeletes[field] = deleteField();
        delete raw[field];
      }
    }

    if (rule.triggerType === 'GOOGLE_REVIEW_DEFAULT') {
      for (const field of EngagementRulesService.REVIEW_TOGGLE_STRIPPED_FIELDS) {
        fieldDeletes[field] = deleteField();
        delete raw[field];
      }
    }

    return {
      ...this.stripUndefined(raw),
      ...fieldDeletes,
      rewardType: deleteField(),
      rewardValue: deleteField(),
      promoCode: deleteField(),
      discountPercent: deleteField(),
      rewardTypeCode: deleteField(),
      storeCreditAmount: deleteField(),
      maxDiscount: deleteField(),
    };
  }

  async update(
    locationId: string,
    ruleId: string,
    partial: Partial<IEngagementRule>,
  ): Promise<void> {
    const existingSnap = await firstValueFrom(
      this.collection(locationId).doc(ruleId).get(),
    );
    const existingData = existingSnap.exists
      ? (existingSnap.data() as IEngagementRule)
      : null;
    const mergedTriggerType = partial.triggerType ?? existingData?.triggerType;
    const mergedVisitNumber =
      partial.visitNumber !== undefined
        ? partial.visitNumber
        : existingData?.visitNumber;

    if (
      partial.triggerType !== undefined ||
      partial.visitNumber !== undefined
    ) {
      await this.assertExclusiveTriggerAllowed(
        locationId,
        {
          triggerType: mergedTriggerType,
          visitNumber: mergedVisitNumber,
        },
        ruleId,
      );
    }

    const { id: _id, ...rest } = partial as IEngagementRule;
    const raw = rest as Record<string, unknown>;

    const fieldDeletes: Record<string, unknown> = {};
    for (const field of EngagementRulesService.NULLABLE_FIELDS) {
      if (field in raw && raw[field] === undefined) {
        fieldDeletes[field] = deleteField();
      }
    }

    if (partial.triggerType === 'GOOGLE_REVIEW_DEFAULT') {
      for (const field of EngagementRulesService.REVIEW_TOGGLE_STRIPPED_FIELDS) {
        fieldDeletes[field] = deleteField();
        delete (raw as Record<string, unknown>)[field];
      }
    }

    const payload = this.stripUndefined(raw);
    await this.collection(locationId)
      .doc(ruleId)
      .update({
        ...payload,
        ...fieldDeletes,
        // Legacy/removed fields — actively delete so re-saved rules drop them.
        rewardType: deleteField(),
        storeCreditAmount: deleteField(),
        maxDiscount: deleteField(),
      });
  }

  async delete(locationId: string, ruleId: string): Promise<void> {
    await this.collection(locationId).doc(ruleId).delete();
  }

  /**
   * Batch-create a set of sensible default rules from the built-in templates.
   * Returns the number of rules created.
   */
  async createDefaultRules(
    locationId: string,
    ruleSet: DefaultRuleSet = 'full_trigger_map',
  ): Promise<number> {
    const reviewChaseDefaults: Omit<IEngagementRule, 'id'>[] = [
      {
        enabled: true,
        triggerType: 'POST_VISIT_HAPPY',
        goal: 'Thank the customer for their positive experience and show genuine appreciation',
        sentiment: 'positive',
        priority: 10,
        keywords: [],
        eligibleOfferIds: [],
        timing: { delayHours: 0 },
      },
      {
        // Per-location review toggle: when enabled, a deterministic rotated
        // Google-review nudge is dispatched as its OWN follow-up message
        // right after the FIRST eligible reply of each visit cycle (sign-up
        // and the first recorded loyalty visit are coalesced into a single
        // cycle), independent of which post-visit rule the AI selected.
        // Subsequent replies on the same visit are silenced by the
        // once-per-visit gate. On the turn the nudge fires the customer sees
        // two outbound bubbles: the reward (any granted-offer wording +
        // portal link) followed by the standalone review-ask. Only `enabled`
        // and `maxNudges` are read at runtime; the goal field is kept for
        // human readability but ignored by the backend.
        enabled: true,
        triggerType: 'GOOGLE_REVIEW_DEFAULT',
        goal:
          'When enabled, the AI Concierge will send a Google review request ' +
          'as a separate follow-up message right after the first eligible ' +
          'reply of each visit cycle, in addition to whichever post-visit ' +
          'rule fired. The customer is asked at most once per visit (sign-up ' +
          'and the first recorded visit count as the same cycle). Use Max ' +
          'review nudges to cap how many different visits will produce a ' +
          'request on this conversation.',
        timing: { delayHours: 0 },
      },
      {
        enabled: true,
        triggerType: 'POST_VISIT_NEUTRAL',
        goal: 'Ask what could have gone better and show genuine interest in improving',
        sentiment: 'neutral',
        priority: 15,
        keywords: ['okay', 'fine', 'alright', 'average', 'not bad', 'decent'],
        eligibleOfferIds: [],
        timing: { delayHours: 0 },
      },
      {
        enabled: true,
        triggerType: 'POST_VISIT_UNHAPPY',
        goal: 'Apologize and offer to make it right',
        sentiment: 'negative',
        priority: 20,
        keywords: [],
        eligibleOfferIds: [],
        timing: { delayHours: 0 },
      },
    ];

    const fullTriggerMapDefaults: Omit<IEngagementRule, 'id'>[] = [
      ...reviewChaseDefaults,
      {
        enabled: true,
        triggerType: 'INACTIVE_WINBACK',
        goal: 'Win back the customer with a personalized message and offer',
        sentiment: 'any',
        priority: 0,
        inactiveDays: 90,
        eligibleOfferIds: [],
        timing: { delayHours: 0 },
      },
      {
        enabled: true,
        triggerType: 'REFERRAL_MILESTONE',
        goal: 'Thank customer for referrals and offer a reward',
        sentiment: 'any',
        priority: 5,
        referralCount: 1,
        eligibleOfferIds: [],
        timing: { delayHours: 0 },
      },
      {
        enabled: true,
        triggerType: 'REFERRED_FRIEND_WELCOME',
        goal: 'Welcome the referred friend and offer a reward for visiting',
        sentiment: 'any',
        priority: 5,
        eligibleOfferIds: [],
        timing: { delayHours: 0 },
      },
      {
        enabled: true,
        triggerType: 'ENROLLMENT_REWARD',
        goal: ENROLLMENT_REWARD_GOAL,
        sentiment: 'any',
        priority: 5,
        eligibleOfferIds: [],
        timing: { delayHours: 0 },
      },
      {
        enabled: true,
        triggerType: 'AMOUNT_SPENT',
        goal: 'Thank the customer for their loyalty and reward them for reaching a spending milestone',
        sentiment: 'any',
        priority: 5,
        amountSpentThreshold: 1500,
        eligibleOfferIds: [],
        timing: { delayHours: 0 },
      },
      {
        enabled: true,
        triggerType: 'OFFER_EXPIRING',
        goal: 'Remind the customer their offer is expiring soon and encourage redemption',
        sentiment: 'any',
        priority: 0,
        reminderDaysBefore: 14,
        eligibleOfferIds: [],
        timing: { delayHours: 0 },
      },
      {
        enabled: true,
        triggerType: 'BIRTHDAY',
        goal: 'Wish the customer a happy birthday and make them feel valued with a special birthday offer',
        birthdayMessage: { ...DEFAULT_BIRTHDAY_MESSAGE },
        sentiment: 'any',
        priority: 5,
        eligibleOfferIds: [],
        timing: { delayHours: 0 },
      },
      {
        enabled: true,
        triggerType: 'VISIT_MILESTONE',
        goal:
          'Celebrate the customer reaching this visit milestone. Acknowledge the visit number, ' +
          'express genuine appreciation for their loyalty, and grant the reward. ' +
          'Regardless of sentiment, always lead with the milestone celebration — ' +
          'if the customer had a bad experience, empathize first then pivot to the milestone.',
        sentiment: 'any',
        priority: 50,
        visitNumber: 5,
        eligibleOfferIds: [],
        timing: { delayHours: 0 },
      },
      {
        enabled: true,
        triggerType: 'VISIT_MILESTONE',
        goal:
          'Celebrate the customer reaching this visit milestone. Acknowledge the visit number, ' +
          'express genuine appreciation for their loyalty and consistency, and grant the reward. ' +
          'Regardless of sentiment, always lead with the milestone celebration — ' +
          'if the customer had a bad experience, empathize first then pivot to the milestone.',
        sentiment: 'any',
        priority: 50,
        visitNumber: 10,
        eligibleOfferIds: [],
        timing: { delayHours: 0 },
      },
    ];

    const defaults =
      ruleSet === 'review_chase' ? reviewChaseDefaults : fullTriggerMapDefaults;

    const col = this.collection(locationId);
    const existingSnap = await firstValueFrom(col.get());
    const existingRules = existingSnap.docs.map((doc) => ({
      id: doc.id,
      ...(doc.data() as IEngagementRule),
    }));
    const claimedRuleIds = new Set<string>();
    let touched = 0;

    for (const rule of defaults) {
      const existingRule = existingRules.find(
        (candidate) =>
          candidate.id &&
          !claimedRuleIds.has(candidate.id) &&
          this.defaultRuleMatches(candidate, rule),
      );

      if (existingRule?.id) {
        await col
          .doc(existingRule.id)
          .update(this.defaultRuleUpdatePayload(rule));
        claimedRuleIds.add(existingRule.id);
        Object.assign(existingRule, rule);
      } else {
        const docRef = await col.add(
          this.stripUndefined(rule as Record<string, unknown>),
        );
        existingRules.push({ id: docRef.id, ...(rule as IEngagementRule) });
        claimedRuleIds.add(docRef.id);
      }
      touched++;
    }
    return touched;
  }
}
